import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { Plus } from 'lucide-react';
import { Turbine } from '../types';
import { listProjects, saveProjectMetadata, generateProjectId, getProjectMetadata } from '../utils/r2Storage';
import TurbineModal from '../components/TurbineModal';

const PageContainer = styled.div`
  padding: 30px;
  max-width: 1600px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  
  div {
    h1 {
      font-size: 32px;
      font-weight: 700;
      color: #1a1f3a;
      margin: 0 0 8px 0;
    }
    
    p {
      color: #6b7280;
      margin: 0;
    }
  }
`;

const AddButton = styled.button`
  background: linear-gradient(135deg, #00d4ff 0%, #0099ff 100%);
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 153, 255, 0.3);
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const Section = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 30px;
  
  h2 {
    font-size: 16px;
    font-weight: 600;
    color: #6b7280;
    margin: 0 0 16px 0;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  
  th {
    text-align: left;
    padding: 12px;
    background: #f9fafb;
    color: #6b7280;
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    border-bottom: 2px solid #e5e7eb;
  }
  
  td {
    padding: 12px;
    border-top: 1px solid #f3f4f6;
    color: #1a1f3a;
    font-size: 14px;
  }
  
  tr {
    cursor: pointer;
    transition: background 0.2s ease;
    
    &:hover {
      background: #f9fafb;
    }
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 60px 20px;
  color: #6b7280;
`;

const TurbinesList: React.FC = () => {
  const navigate = useNavigate();
  const [turbines, setTurbines] = useState<Turbine[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadTurbines();
  }, []);

  const loadTurbines = async () => {
    try {
      // Load turbines from R2
      const metadata: any = await getProjectMetadata('turbines-list');
      if (metadata && metadata.turbines) {
        setTurbines(metadata.turbines);
      } else {
        setTurbines([]);
      }
    } catch (error) {
      console.error('Failed to load turbines:', error);
      setTurbines([]);
    } finally {
      setLoading(false);
    }
  };

  const handleTurbineClick = (turbine: Turbine) => {
    // Always navigate to turbine detail page
    navigate(`/turbines/${turbine.id}`);
  };

  const handleAddTurbine = () => {
    setIsModalOpen(true);
  };

  const handleSaveTurbine = async (turbineData: Omit<Turbine, 'id' | 'createdAt' | 'farmId'>) => {
    try {
      const newTurbine: Turbine = {
        ...turbineData,
        id: generateProjectId('turbine'),
        farmId: 'default',
        createdAt: new Date().toISOString(),
      };

      const updatedTurbines = [...turbines, newTurbine];
      setTurbines(updatedTurbines);

      // Save to R2
      await saveProjectMetadata('turbines-list', { turbines: updatedTurbines } as any);
      setIsModalOpen(false);
    } catch (error) {
      console.error('Failed to save turbine:', error);
      alert('Türbin kaydedilemedi. Lütfen tekrar deneyin.');
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <div style={{
            border: '4px solid #f3f3f3',
            borderTop: '4px solid #0099ff',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }} />
          <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
          <p style={{ color: '#6b7280' }}>Loading turbines...</p>
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <Header>
        <div>
          <h1>TURBINES</h1>
          <p>List of all inspection projects</p>
        </div>
        <AddButton onClick={handleAddTurbine}>
          <Plus />
          Add Turbine
        </AddButton>
      </Header>

      {turbines.length > 0 ? (
        <Section>
          <h2>Turbine List</h2>
          <p style={{ color: '#6b7280', fontSize: '14px', marginBottom: '16px' }}>
            Click on a turbine to view details.
          </p>
          <Table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Turbine Number</th>
                <th>Name</th>
                <th>Model</th>
                <th>Linked Inspection</th>
              </tr>
            </thead>
            <tbody>
              {turbines.map((turbine, index) => (
                <tr key={turbine.id} onClick={() => handleTurbineClick(turbine)}>
                  <td>{index + 1}</td>
                  <td>{turbine.number}</td>
                  <td>{turbine.name}</td>
                  <td>{turbine.model}</td>
                  <td>{turbine.inspectionId ? turbine.inspectionId : 'Not linked'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Section>
      ) : (
        <Section>
          <EmptyState>
            <h2>No turbines yet</h2>
            <p>Add turbine data to get started</p>
            <AddButton onClick={handleAddTurbine} style={{ margin: '20px auto 0' }}>
              <Plus />
              Add Turbine
            </AddButton>
          </EmptyState>
        </Section>
      )}

      <TurbineModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSave={handleSaveTurbine}
      />
    </PageContainer>
  );
};

export default TurbinesList;
